import React, { FC, useState, useEffect } from 'react';
import { Form, Radio, Divider, Header, Icon, Button, Message, Modal } from 'semantic-ui-react';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { BasicDialogContainer } from '@src/views/components/modals/BasicDialogContainer';
import { StyledContainer, Flexbox, FlexItem } from '@src/views/containers';
import { TextField, TextAreaField } from '@src/views/components/forms';
import { ItemWithIcon } from '@src/views/components/display/ItemWithIcon';
import { Size, Align, FlexAlign, FlexJustify, Uuid } from '@src/types';
import { useCurrentUser, useSystemModals, useSystemToasts, useYupExtensions } from '@src/hooks';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { PROJECT_ROLES } from '@src/constants/ProductEnums';

interface ImportDataDialogProps {
    projectId: Uuid;
    projectVersionId: Uuid;
    onComplete?: (result: any) => void;
    onClose?: () => void;
}

enum IMPORT_MODE {
    NEW_VERSION = 'NEW_VERSION',
    EXISTING_VERSION = 'EXISTING_VERSION',
}

interface ImportFormValues {
    importMode: IMPORT_MODE;
    versionName: string;
    versionDescription: string;
}

const ImportDataDialog: FC<ImportDataDialogProps> = (props) => {
    const { projectId, projectVersionId, onComplete, onClose } = props;

    const { currentUser } = useCurrentUser();
    const modalActions = useSystemModals();

    const isViewer = currentUser?.projects?.find(
        ( p: { projectId: Uuid; projectRole: string } ) => p.projectId === projectId
    )?.projectRole === PROJECT_ROLES.VIEWER;
    const toastActions = useSystemToasts();
    const { importStaticProjectActivities, downloadStaticProjectActivityTemplate } = useCentralCalls();
    const { addStringMinCharDescriptorTest } = useYupExtensions();

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importResults, setImportResults] = useState<any>(null);
    const [importError, setImportError] = useState<string | null>(null);
    const [filePreview, setFilePreview] = useState<any[]>([]);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    addStringMinCharDescriptorTest();

    const initialValues: ImportFormValues = {
        importMode: IMPORT_MODE.EXISTING_VERSION,
        versionName: '',
        versionDescription: '',
    };

    const validationSchema = Yup.object().shape({
        importMode: Yup.string().required(),
        versionName: Yup.string().when('importMode', {
            is: IMPORT_MODE.NEW_VERSION,
            then: Yup.string().required('Version name is required'),
            otherwise: Yup.string(),
        }),
        versionDescription: Yup.string(),
    });

    const handleFileSelect = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.xlsx,.xls,.csv';
        input.onchange = () => {
            if (input.files && input.files.length > 0) {
                const file = input.files[0];
                setSelectedFile(file);
                setImportResults(null); // Clear results on new file selection
                setImportError(null);   // Clear errors on new file selection
                setFilePreview([]);     // Clear preview on new file selection

                // Generate preview of the file
                generateFilePreview(file);
            }
        };
        input.click();
    };
    const excelSerialToDateString = (serial: number) => {
        // Convert Excel serial date -> "YYYY-MM-DD"
        const excelEpochOffset = 25569; // Excel 1900 epoch to Unix epoch
        const msPerDay = 86400 * 1000;
        const ms = (serial - excelEpochOffset) * msPerDay;
        const d = new Date(ms);

        const yyyy = d.getUTCFullYear();
        const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(d.getUTCDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const formatCellForPreview = (value: any): string => {
        if (value == null) return '';

        // If xlsx already parsed it as a Date
        if (value instanceof Date) {
            const yyyy = value.getFullYear();
            const mm = String(value.getMonth() + 1).padStart(2, '0');
            const dd = String(value.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        }

        // If it's a number that looks like an Excel date serial, interpret it
        if (typeof value === 'number' && value > 25000 && value < 60000) {
            return excelSerialToDateString(value);
        }

        // Otherwise just toString
        return String(value);
    };
    const generateFilePreview = async (file: File) => {
        setIsLoadingPreview(true);
        try {
            if (file.name.toLowerCase().endsWith('.csv')) {
                const text = await file.text();
                const lines = text.split(/\r?\n/).filter(l => l.length > 0);
                const header = lines[0].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
                const headerLength = header.length;
                const previewData = lines.map((line, i) => {
                    let values = line
                        .split(',')
                        .map(v => v.trim().replace(/^"|"$/g, ''));
                    // Pad with empty strings if fewer columns than header
                    while (values.length < headerLength) values.push('');
                    return { row: i + 1, data: values };
                });
                setFilePreview(previewData);
            } else {
                // Excel (.xlsx / .xls)
                const XLSX = await import('xlsx');
                const arrayBuffer = await file.arrayBuffer();

                // tell xlsx to parse dates into JS Date where it can
                const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
                const sheet = wb.SheetNames[0];
                const worksheet = wb.Sheets[sheet];

                // header:1 => array-of-arrays
                // raw:true => don't force string formatting, keep native types
                const aoa: any[][] = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    raw: true
                });

                // Align rows to the header columns so empty middle cells stay empty
                const headerLength = aoa.length > 0 ? (aoa[0]?.length ?? 0) : 0;
                const previewData = aoa.map((row, i) => {
                    const aligned: any[] = new Array(headerLength).fill(undefined);
                    if (row && row.length > 0) {
                        for (let c = 0; c < headerLength; c++) {
                            aligned[c] = c < row.length ? row[c] : undefined;
                        }
                    }
                    return {
                        row: i + 1,
                        data: aligned.map(cellVal => formatCellForPreview(cellVal))
                    };
                });

                setFilePreview(previewData);
            }
        } catch (error) {
            console.error('Error generating preview:', error);
            setFilePreview([]);
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            await downloadStaticProjectActivityTemplate('xlsx', true);
        } catch (err) {
            modalActions.show(
                <StyledContainer padding={Size.SM}>
                    <ItemWithIcon iconName="exclamation triangle" iconColor="red" title="Download failed">
                        {(err as Error).message}
                    </ItemWithIcon>
                </StyledContainer>,
                { size: 'tiny', centered: true }
            );
        }
    };

    const handleImport = async (values: ImportFormValues) => {
        if (!selectedFile) {
            setImportError('Please select a file to import.');
            return;
        }

        setIsImporting(true);
        setImportError(null);
        setImportResults(null);

        try {
            const result = await importStaticProjectActivities(
                selectedFile,
                projectId,
                String(projectVersionId ?? ''),
                currentUser.id,
                values.versionName,
                values.versionDescription
            );

            setImportResults(result);
            console.log("RESULTSSSS");
            console.log(result);
            // Show success toast
            toastActions.success('Import completed', 'Data has been successfully imported to the project.');

            onComplete?.(result);
        } catch (err) {
            setImportError((err as Error).message || 'An error occurred during import. Please check your file format and try again.');
        } finally {
            setIsImporting(false);
        }
    };

    // derive relationship failures from result (support PascalCase and camelCase from backend)
    const relationshipFailures: any[] = importResults
        ? (importResults.RelationshipFailures ?? importResults.relationshipFailures ?? [])
        : [];

    const relationshipFailuresCount = Array.isArray(relationshipFailures) ? relationshipFailures.length : 0;

    const pickField = (obj: any, fieldBase: string) => {
        if (!obj) return '';
        return (
            obj[fieldBase] ??
            obj[fieldBase.charAt(0).toLowerCase() + fieldBase.slice(1)] ??
            obj[fieldBase.toLowerCase()] ??
            ''
        );
    };

    return (
        <Formik<ImportFormValues>
            initialValues={initialValues}
            validateOnChange={true}
            validationSchema={validationSchema}
            onSubmit={handleImport}
        >
            {(formik: FormikProps<ImportFormValues>) => (
                <>
                    <Form onSubmit={formik.handleSubmit}>
                        <BasicDialogContainer
                            title="Import Project Data"
                            roundedTop
                            contentPadding={[Size.MD, Size.MD, Size.LG, Size.MD]}
                            onClose={onClose}
                        >
                            {/* Download Template Section */}
                            <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                <div style={{ marginBottom: '0.5rem', fontSize: '0.9em', color: '#666', fontStyle: 'italic' }}>
                                    (Download Template For Upload)
                                </div>
                                <Button
                                    type="button"
                                    icon="download"
                                    content="Download Template"
                                    basic
                                    color="purple"
                                    onClick={handleDownloadTemplate}
                                    disabled={isViewer}
                                    fluid
                                />
                            </StyledContainer>

                            <Divider />

                            {/* File Selection Section */}
                            <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                <Header as="h4" style={{ marginBottom: '0.5rem' }}>
                                    <Icon name="file excel" color="green" />
                                    Select Import File
                                </Header>
                                <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.SPACE_BETWEEN}>
                                    <FlexItem grow>
                                        <StyledContainer
                                            padding={Size.SM}
                                            style={{
                                                border: '1px dashed #ccc',
                                                borderRadius: '4px',
                                                backgroundColor: '#f9f9f9',
                                            }}
                                        >
                                            {selectedFile ? (
                                                <ItemWithIcon iconName="file" iconColor="blue" title={selectedFile.name}>
                                                    {(selectedFile.size / 1024).toFixed(2)} KB
                                                </ItemWithIcon>
                                            ) : (
                                                <em style={{ color: '#999' }}>No file selected</em>
                                            )}
                                        </StyledContainer>
                                    </FlexItem>
                                    <FlexItem>
                                        <Button
                                            type="button"
                                            icon="upload"
                                            content="Choose File"
                                            color="purple"
                                            onClick={handleFileSelect}
                                            disabled={isViewer}
                                            style={{ marginLeft: '1rem' }}
                                        />
                                    </FlexItem>
                                </Flexbox>
                            </StyledContainer>

                            <Divider />

                            {/* Import Mode Selection */}
                            <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                <Header as="h4" style={{ marginBottom: '1rem' }}>
                                    <Icon name="settings" color="purple" />
                                    Import Options
                                </Header>

                                <Form.Field>
                                    <Radio
                                        label="Create New Project Version"
                                        name="importMode"
                                        value={IMPORT_MODE.NEW_VERSION}
                                        checked={formik.values.importMode === IMPORT_MODE.NEW_VERSION}
                                        onChange={() => formik.setFieldValue('importMode', IMPORT_MODE.NEW_VERSION)}
                                        disabled={isViewer}
                                    />
                                    <div style={{ marginLeft: '1.5rem', marginTop: '0.25rem', fontSize: '0.9em', color: '#666' }}>
                                        Import data into a new version of the current project
                                    </div>
                                </Form.Field>

                                <Form.Field style={{ marginTop: '1rem' }}>
                                    <Radio
                                        label="Add to Existing Version"
                                        name="importMode"
                                        value={IMPORT_MODE.EXISTING_VERSION}
                                        checked={formik.values.importMode === IMPORT_MODE.EXISTING_VERSION}
                                        onChange={() => formik.setFieldValue('importMode', IMPORT_MODE.EXISTING_VERSION)}
                                        disabled={isViewer}
                                    />
                                    <div style={{ marginLeft: '1.5rem', marginTop: '0.25rem', fontSize: '0.9em', color: '#666' }}>
                                        Add imported data to the current project version
                                    </div>
                                </Form.Field>
                            </StyledContainer>

                        <Divider />

                            {/* Conditional Form Fields Based on Import Mode */}
                            {formik.values.importMode === IMPORT_MODE.NEW_VERSION ? (
                                <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                    <Header as="h4" style={{ marginBottom: '1rem' }}>
                                        <Icon name="plus square" color="purple" />
                                        New Version Details
                                    </Header>
                                    <TextField
                                        name="versionName"
                                        label="Version Name"
                                        placeholder="e.g., Imported Version - Q4 2025"
                                        required
                                    />
                                    <TextAreaField
                                        name="versionDescription"
                                        label="Description"
                                        placeholder="Describe this imported version..."
                                    />
                                </StyledContainer>
                            ) : (
                                <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                    <Message info>
                                        <Message.Header>
                                            <Icon name="info circle" />
                                            Adding to Existing Version
                                        </Message.Header>
                                        <p>
                                            Data will be imported and added to the current project version. Existing data will not be modified.
                                        </p>
                                    </Message>
                                </StyledContainer>
                            )}

                            <Divider />
                            {/* Import Preview Section */}
                            {!importResults ? (
                                <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                    <Header as="h4" style={{ marginBottom: '1rem' }}>
                                        <Icon name="eye" color="blue" />
                                        Data Preview
                                    </Header>

                                    {isLoadingPreview ? (
                                        <Message info>
                                            <Message.Header>
                                                <Icon name="spinner" loading />
                                                Loading Preview...
                                            </Message.Header>
                                        </Message>
                                    ) : filePreview.length > 0 ? (
                                        <div style={{
                                            border: '1px solid #e1e5e9',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            backgroundColor: '#fafbfc'
                                        }}>
                                            {/* Preview Header */}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    padding: '0.75rem 1rem',
                                                    borderBottom: '1px solid #e1e5e9',
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            >
                                                <Icon name="table" color="blue" />
                                                <span style={{ fontSize: '1.05rem', fontWeight: 700, letterSpacing: 0.2 }}>
                                                    File Preview
                                                </span>
                                                <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#6c757d' }}>
                                                    {Math.max(0, filePreview.length - 1)}
                                                </span>
                                            </div>

                                            <div
                                                style={{
                                                    height: 250,                // fixed height
                                                    overflowY: 'auto',          // vertical scroll
                                                    // ensure horizontal scroll can appear if columns exceed width
                                                    width: '100%',
                                                }}
                                            >
                                                {filePreview.map((row, index) => (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            display: 'flex',
                                                            borderBottom: index < filePreview.length - 1 ? '1px solid #e1e5e9' : 'none',
                                                            backgroundColor: index === 0 ? '#f1f3f4' : 'white',
                                                            // make the row width expand to fit cells; wrapper handles horizontal scroll
                                                            width: 'max-content'
                                                        }}
                                                    >
                                                        {/* Row number */}
                                                        <div
                                                            style={{
                                                                width: 60,
                                                                padding: '0.5rem',
                                                                backgroundColor: '#f8f9fa',
                                                                borderRight: '1px solid #e1e5e9',
                                                                fontSize: '0.85em',
                                                                color: '#6c757d',
                                                                textAlign: 'center',
                                                                fontWeight: index === 0 ? 'bold' : 'normal',
                                                                position: 'sticky', left: 0   // optional: sticky row numbers
                                                            }}
                                                        >
                                                            {row.row}
                                                        </div>

                                                        {/* Data cells */}
                                                        {row.data.map((cell: string, cellIndex: number) => (
                                                            <div
                                                                key={cellIndex}
                                                                style={{
                                                                    // fixed min width per column so content lays out horizontally
                                                                    flex: '0 0 200px',
                                                                    padding: '0.5rem',
                                                                    borderRight: cellIndex < row.data.length - 1 ? '1px solid #e1e5e9' : 'none',
                                                                    fontSize: '0.85em',
                                                                    fontWeight: index === 0 ? 'bold' : 'normal',
                                                                    color: index === 0 ? '#495057' : '#374151',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}
                                                                title={cell} // tooltip with full value
                                                            >
                                                                {cell}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Footer */}
                                            <div
                                                style={{
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '0.5rem',
                                                    fontSize: '0.8em',
                                                    color: '#6c757d',
                                                    textAlign: 'center',
                                                    borderTop: '1px solid #e1e5e9'
                                                }}
                                            >
                                                Showing {filePreview.length} rows
                                            </div>
                                        </div>
                                    ) : selectedFile ? (
                                        <Message warning>
                                            <Message.Header>
                                                <Icon name="exclamation triangle" />
                                                Unable to Preview
                                            </Message.Header>
                                            <p>
                                                Could not generate preview for this file. The import will still work -
                                                data will be imported and added to the current project version.
                                            </p>
                                        </Message>
                                    ) : (
                                        <Message info>
                                            <Message.Header>
                                                <Icon name="info circle" />
                                                Ready to Import
                                            </Message.Header>
                                            <p>
                                                Select a file to see a preview of the data that will be imported.
                                                Data will be added to the current project version without modifying existing data.
                                            </p>
                                        </Message>
                                    )}
                                </StyledContainer>
                            ) : (
                                <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>

                                    <div style={{
                                        border: '1px solid #e1e5e9',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        backgroundColor: '#fafbfc'
                                    }}>
                                        {/* Results Header */}
                                        <div style={{
                                            backgroundColor: '#f8f9fa',
                                            padding: '0.75rem',
                                            borderBottom: '1px solid #e1e5e9',
                                            fontWeight: 'bold',
                                            color: '#495057'
                                        }}>
                                            <Icon name="check circle" color="green" />
                                            Import Completed Successfully
                                        </div>

                                        {/* Results Content */}
                                        <div style={{
                                            padding: '1rem',
                                            backgroundColor: '#fafbfc'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '0.5rem',
                                                fontSize: '0.95em',
                                                color: '#374151'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '0.5rem',
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '4px',
                                                    border: '1px solid #e1e5e9'
                                                }}>
                                                    <span style={{ fontWeight: 'bold' }}>Created Categories:</span>
                                                    <span style={{ fontWeight: 'bold', color: '#111827' }}>{importResults.CreatedCategories || 0}</span>
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '0.5rem',
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '4px',
                                                    border: '1px solid #e1e5e9'
                                                }}>
                                                    <span style={{ fontWeight: 'bold' }}>Created SubCategories:</span>
                                                    <span style={{ fontWeight: 'bold', color: '#111827' }}>{importResults.CreatedSubCategories || 0}</span>
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '0.5rem',
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '4px',
                                                    border: '1px solid #e1e5e9'
                                                }}>
                                                    <span style={{ fontWeight: 'bold' }}>Created SubObjectives:</span>
                                                    <span style={{ fontWeight: 'bold', color: '#111827' }}>{importResults.CreatedSubObjectives || 0}</span>
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '0.5rem',
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '4px',
                                                    border: '1px solid #e1e5e9'
                                                }}>
                                                    <span style={{ fontWeight: 'bold' }}>Created Activities:</span>
                                                    <span style={{ fontWeight: 'bold', color: '#111827' }}>{importResults.CreatedActivities || 0}</span>
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '0.5rem',
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '4px',
                                                    border: '1px solid #e1e5e9'
                                                }}>
                                                    <span style={{ fontWeight: 'bold' }}>Created Static Project Activities:</span>
                                                    <span style={{ fontWeight: 'bold', color: '#111827' }}>{importResults.CreatedStaticProjectActivities || 0}</span>
                                                </div>

                                                {/* Relationship failures summary */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '0.5rem',
                                                    backgroundColor: '#fff5f5',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ffe4e6'
                                                }}>
                                                    <span style={{ fontWeight: 'bold', color: '#b91c1c' }}>Failed Relationship Imports:</span>
                                                    <span style={{ fontWeight: 'bold', color: '#b91c1c' }}>{relationshipFailuresCount}</span>
                                                </div>

                                                {/* If any failures, show details */}
                                                {relationshipFailuresCount > 0 ? (
                                                    <div style={{
                                                        marginTop: 8,
                                                        border: '1px solid #fde2e2',
                                                        borderRadius: 6,
                                                        maxHeight: 220,
                                                        overflowY: 'auto',
                                                        background: '#fff'
                                                    }}>
                                                        {relationshipFailures.map((f, i) => (
                                                            <div key={i} style={{ padding: 8, borderBottom: i < relationshipFailuresCount - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                                                <div style={{ fontWeight: 700, color: '#111827' }}>{pickField(f, 'ChildActivityName') || pickField(f, 'childActivityName') || `Child #${i+1}`}</div>
                                                                <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                                                                    <div><strong>ParentActivityName:</strong> {pickField(f, 'ParentActivityName')}</div>
                                                                    <div><strong>Reason:</strong> {pickField(f, 'Reason')}</div>
                                                                    <div><strong>MatchesFound:</strong> {pickField(f, 'MatchesFound') ?? ''}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div style={{ marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 6, border: '1px solid #e1e5e9' }}>
                                                        <em>0 failed relationship imports</em>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Results Footer */}
                                        <div style={{
                                            backgroundColor: '#f8f9fa',
                                            padding: '0.5rem',
                                            fontSize: '0.8em',
                                            color: '#6c757d',
                                            textAlign: 'center',
                                            borderTop: '1px solid #e1e5e9'
                                        }}>
                                            Import completed successfully
                                        </div>
                                    </div>
                                </StyledContainer>
                            )}


                            {/* Import Error Display */}
                            {importError && (
                                <>
                                    <Divider />
                                    <StyledContainer padding={[Size.SM, 0]}>
                                        <Message error>
                                            <Message.Header>
                                                <Icon name="exclamation triangle" />
                                                Import Failed
                                            </Message.Header>
                                            <Message.Content>{importError}</Message.Content>
                                        </Message>
                                    </StyledContainer>
                                </>
                            )}

                            {/* Import Action Button - Only show if no results yet */}
                            {!importResults && (
                                <>
                                    <Divider />
                                    { isViewer ? (
                                        <Message info>
                                            <Icon name="lock" />
                                            You have view-only access to this project. Contact an administrator to import data.
                                        </Message>
                                    ) : (
                                        <StyledContainer textAlign={Align.RIGHT}>
                                            <Button
                                                type="submit"
                                                color="purple"
                                                content="Import Data"
                                                loading={isImporting}
                                                disabled={!formik.isValid || !selectedFile || isImporting}
                                            />
                                        </StyledContainer>
                                    ) }
                                </>
                            )}

                        </BasicDialogContainer>
                    </Form>

                    <Modal.Actions>
                        <StyledContainer textAlign={Align.RIGHT}>
                            <Button
                                type="button"
                                basic
                                content="Cancel"
                                onClick={onClose}
                            />
                            <Button
                                type="button"
                                primary
                                content="Ok"
                                onClick={onClose}
                            />
                        </StyledContainer>
                    </Modal.Actions>
                </>
            )}
        </Formik>
    );
};

export { ImportDataDialog };